import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { BillFile, Bills, Cfdi, DockingAndMooring, ManeuverBill, ManeuverBillResponse, PaymentConditions, PaymentMethod, TypeCurrency } from 'src/app/interfaces/billing.interfaces';

/**
 * Servicio que gestiona la facturación de maniobras, atraques y amarres,
 * incluyendo la creación y consulta de facturas, sus archivos y los catálogos
 * relacionados (CFDI, tipos de moneda, condiciones y métodos de pago).
 */
@Injectable({
  providedIn: 'root'
})
export class BillingService {
  bills: Bills[] = [];
  billsFiltered: Bills[] = [];
  getBillsHasBeenCalled: boolean = false;

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Crea una factura de maniobra o de atraque y amarre.
   * @param bill - Datos de la factura a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createBill(bill: ManeuverBill | DockingAndMooring): Observable<ManeuverBillResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.billing.create}`;
    return this.http.post<ManeuverBillResponse>(url, bill);
  }

  /**
   * Obtiene las facturas de un barco, asignando un identificador único a cada
   * registro de la lista.
   * @param boat_number - Número del barco.
   * @param date - Fecha opcional para filtrar las facturas.
   * @returns Observable con la lista de facturas.
   */
  getBills(boat_number: number, date?: string): Observable<Bills[]> {
    this.getBillsHasBeenCalled = true;
    let url: string = `${GLOBAL.url}${GLOBAL.billing.list}${boat_number}`;
    if (date) {
      url.concat(date);
    }
    return this.http.get<Bills[]>(url)
    .pipe(
      map((bills) => {
        bills.forEach((item, index) => {
          item.uid = index + 1;
        });
        return bills;
      })
    );
  }

  /**
   * Obtiene el archivo asociado a una factura.
   * @param invoice_folio - Folio de la factura.
   * @returns Observable con el archivo de la factura.
   */
  getBillFile(invoice_folio: string): Observable<BillFile> {
    const url: string = `${GLOBAL.url}${GLOBAL.billing.file}`;
    return this.http.post<BillFile>(url, { invoice_folio, document_type: 1 });
  }

  /**
   * Obtiene el catálogo de usos de CFDI.
   * @returns Observable con la lista de CFDI.
   */
  getCfdiList(): Observable<Cfdi[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.billing.cfdiList}`;
    return this.http.get<Cfdi[]>(url);
  }

  /**
   * Obtiene el catálogo de tipos de moneda.
   * @returns Observable con la lista de tipos de moneda.
   */
  getTypeCurrencyList(): Observable<TypeCurrency[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.billing.typeCurrency}`;
    return this.http.get<TypeCurrency[]>(url);
  }

  /**
   * Obtiene el catálogo de condiciones de pago.
   * @returns Observable con la lista de condiciones de pago.
   */
  getPaymentConditionsList(): Observable<PaymentConditions[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.billing.paymentConditions}`;
    return this.http.get<PaymentConditions[]>(url);
  }

  /**
   * Obtiene el catálogo de métodos de pago.
   * @returns Observable con la lista de métodos de pago.
   */
  getPaymentMethodList(): Observable<PaymentMethod[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.billing.paymentMethod}`;
    return this.http.get<PaymentMethod[]>(url);
  }

  /**
   * Ordena un arreglo de objetos de forma ascendente según la clave indicada.
   * @param array - Arreglo a ordenar.
   * @param key - Clave del objeto por la que se ordena.
   * @returns El arreglo ordenado.
   */
  sortArray<T>(array: T[], key: keyof T): T[] {
    return array.sort(function (a, b) {
      const valueA = String(a[key]).toLowerCase();
      const valueB = String(b[key]).toLowerCase();
      if (valueA > valueB) {
        return 1;
      }
      if (valueA < valueB) {
        return -1;
      }
      return 0;
    });
  }
}
